import { Reveal, Label } from "./Reveal";

// Desired-outcome chapter: what better sleep can actually look like at home.
// Proof (JayeProof) renders immediately after this section in App.js. No CTA here —
// the Assessment section that follows the proof carries the conversion path.
const OUTCOMES = [
  "You put them down — and they stay asleep.",
  "You walk out of the room without holding your breath.",
  "A 32-minute nap starts becoming an actual nap.",
  "They wake during the night — and get back to sleep without needing the entire bedtime routine again.",
  "Your toddler hears \u201cgoodnight\u201d — and bedtime actually ends.",
  "You and your partner actually finish dinner.",
];

const SKILLS = ["Falling asleep", "Staying asleep", "Getting back to sleep"];

export const Goals = () => (
  <section id="working-toward" data-testid="working-toward-section" className="bg-ivory overflow-x-clip">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-24 lg:py-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <Label>What better sleep can look like</Label>
            </Reveal>
            <Reveal as="h2" delay={0.08} data-testid="working-toward-headline" className="mt-6 font-serif font-light text-ink text-4xl sm:text-5xl leading-[1.08] tracking-tight">
              What would easier
              <span className="block italic text-clay">look like at your house?</span>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal>
            <div className="space-y-4 max-w-xl">
              {OUTCOMES.map((o, i) => (
                <p key={i} data-testid={`outcome-${i}`} className="flex items-baseline gap-4 text-base sm:text-lg font-normal text-smoke">
                  <span aria-hidden="true" className="h-px w-5 shrink-0 bg-clay translate-y-[-5px]" />
                  {o}
                </p>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <p data-testid="working-toward-emphasis" className="mt-14 font-serif font-light text-ink text-3xl sm:text-4xl leading-[1.15] tracking-tight">
              That&rsquo;s the change
              <span className="block italic text-clay mt-1">we&rsquo;re working toward.</span>
            </p>
            <p className="mt-8 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-lg">
              Not one lucky night.
              <span className="block">A new way of sleeping that your child is learning.</span>
            </p>
          </Reveal>
        </div>
      </div>

      {/* The three skills — a pure typography moment, no cards, no imagery */}
      <Reveal className="mt-24 lg:mt-36">
        <p data-testid="sleep-skills-moment" className="font-serif font-light text-ink text-[1.85rem] sm:text-6xl lg:text-7xl leading-[1.14] tracking-tight">
          {SKILLS.map((s, i) => (
            <span key={s} className={`block ${i === 1 ? "pl-5 sm:pl-[9%]" : i === 2 ? "pl-10 sm:pl-[18%]" : ""}`}>
              {s}
              <span className="text-clay">.</span>
            </span>
          ))}
        </p>
      </Reveal>

      <Reveal delay={0.1} className="mt-10 lg:mt-14">
        <p className="font-serif italic font-light text-ink text-xl sm:text-2xl lg:text-3xl leading-snug tracking-tight max-w-xl">
          With less and less help from you.
        </p>
        <p className="mt-6 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-lg">
          You go to bed without wondering how many times you&rsquo;ll be back
          up. And when something does change — a nap gets weird, bedtime
          shifts, a tooth comes in — you understand your child&rsquo;s sleep
          well enough that you&rsquo;re not starting over.
        </p>
      </Reveal>

      <Reveal delay={0.16} className="mt-16 lg:mt-24">
        <p data-testid="working-toward-closer" className="font-serif font-light text-ink text-3xl sm:text-4xl lg:text-[2.8rem] leading-[1.14] tracking-tight max-w-3xl">
          Your child knows how to sleep.
          <span className="block mt-4 italic text-clay">And you know how to help them</span>
          <span className="block italic text-clay">keep doing it.</span>
        </p>
      </Reveal>
    </div>
  </section>
);
